import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UnredeemedCountService } from '@fgb/core';
import gsap from 'gsap';
import { filter } from 'rxjs';

@Component({
  selector: 'fgb-mixed-loyalty-nav',
  templateUrl: './mixed-loyalty-nav.component.html',
  styleUrls: ['./mixed-loyalty-nav.component.scss'],
})
export class MixedLoyaltyNavComponent implements OnInit {
  public readonly TAB_CONFIG: Record<string, { label: string }> = {
    marketplace: { label: 'rewards.nav.marketplace' },
    events: { label: 'rewards.nav.events' },
    raffles: { label: 'rewards.nav.lotto' },
    auctions: { label: 'rewards.nav.auctions' },
    wallet: { label: 'rewards.nav.wallet' },
  };

  private readonly routeToTabIndexMap: Record<string, number> = {
    marketplace: 0,
    events: 1,
    raffles: 2,
    auctions: 3,
    wallet: 4,
  };

  unredeemedCount = this.unredeemedCountService.unredeemedWalletCount;
  constructor(
    private unredeemedCountService: UnredeemedCountService,
    private router: Router
  ) {}

  ngOnInit() {
    this.unredeemedCountService.fetchUnredeemedWalletCount();
  }

  @Input() selectedIndex = 0;
  @Input() minWidth: number | 'auto' = 'auto';

  @Input() tabs = ['marketplace', 'events', 'raffles', 'auctions', 'wallet'];

  @ViewChild('indicator', { static: true }) indicatorRef!: ElementRef;
  @ViewChild('tabsContainer', { static: true }) tabsContainerRef!: ElementRef;
  @Output() tabSelectionChange: EventEmitter<number> = new EventEmitter<number>();

  ngAfterViewInit(): void {
    this.syncTabWithRoute();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.syncTabWithRoute();
    });
  }

  private syncTabWithRoute(): void {
    const index = this.getActiveTabIndexFromUrl();

    if (index === null) {
      return;
    }

    this.selectedIndex = index;

    // Wait for DOM reflow AFTER nav is attached
    setTimeout(() => {
      const container = this.tabsContainerRef?.nativeElement;
      if (!container || !container.children[length]) {
        return;
      }

      this.resetIndicator();
      this.animateIndicator(index);
      this.scrollTabIntoView(index);
    });
  }

  private getActiveTabIndexFromUrl(): number | null {
    const url = this.router.url;

    for (const routeKey of Object.keys(this.routeToTabIndexMap)) {
      if (url.includes(`/rewards/${routeKey}`)) {
        return this.routeToTabIndexMap[routeKey];
      }
    }

    return null;
  }

  selectTab(index: number): void {
    this.animateIndicator(index);

    this.scrollTabIntoView(index);

    this.selectedIndex = index;
    this.tabSelectionChange.emit(index);
  }

  private resetIndicator(): void {
    gsap.set(this.indicatorRef.nativeElement, {
      x: 0,
      width: 0,
      scale: 1,
    });
  }

  private scrollTabIntoView(index: number): void {
    const container = this.tabsContainerRef.nativeElement as HTMLElement;
    const tab = container.children[index + 1] as HTMLElement;

    if (!tab) return;

    tab.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }

  private animateIndicator(index: number): void {
    const container = this.tabsContainerRef.nativeElement;

    const tab = container.children[index + 1] as HTMLElement;

    if (!tab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const indicator = this.indicatorRef.nativeElement;

    const finalX = tabRect.left - containerRect.left + container.scrollLeft;

    gsap.to(indicator, {
      duration: 0.5,
      x: finalX,
      ease: 'back.out(1.3)',
      onStart: () => {
        gsap.to(indicator, {
          duration: 0.3,
          width: tabRect.width,
          ease: 'power2.out',
        });
      },
    });

    gsap.fromTo(indicator, { scale: 0.95 }, { scale: 1, duration: 0.3, ease: 'power2.out' });
  }

  get tabStyle(): { [key: string]: string } {
    return {
      minWidth: this.minWidth === 'auto' ? 'auto' : `${this.minWidth}px`,
    };
  }
}
